import { HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

type CardProps = HTMLAttributes<HTMLDivElement>;

/**
 * A container component that provides consistent styling for card-based layouts.
 *
 * Cards are bordered containers with rounded corners and a background color that
 * adapts to the current theme. They're useful for grouping related content and
 * creating visual hierarchy in your interface.
 *
 * **Default behavior:**
 * - Takes full width of parent container (`w-full`)
 * - Clips overflowing content (`overflow-clip`)
 * - Has rounded corners (`rounded-xl`) and a border
 *
 * @component
 * @example
 * ```tsx
 * // Basic card
 * <Card>
 *   <div className="p-6">
 *     <h3>Card Title</h3>
 *     <p>Card content goes here</p>
 *   </div>
 * </Card>
 *
 * // Card with custom width
 * <Card className="w-96">
 *   <div className="p-4">Fixed width content</div>
 * </Card>
 *
 * // Clickable card using Button component
 * <Button variant="ghost" size="none" onClick={handleClick}>
 *   <Card>
 *     <div className="p-5">Clickable content</div>
 *   </Card>
 * </Button>
 * ```
 *
 * @param {HTMLAttributes<HTMLDivElement>} props - All standard div attributes are supported
 * @param {ReactNode} props.children - Content to render inside the card
 * @param {string} [props.className] - Additional CSS classes to merge with default card styles
 */
export const Card = ({ children, className, ...props }: CardProps) => {
  return (
    <div className={cn('w-full overflow-clip rounded-lg border border-border bg-card', className)} {...props}>
      {children}
    </div>
  );
};
